// src/pages/dashboard/branch/analytics/components/ItemBreakdownTable.tsx
import React, { memo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { FiPackage } from "react-icons/fi";
import Card from "../../../../../components/ui/Card";
import Skeleton from "../../../../../components/ui/Skeleton";
import { MenuItem } from "../../../../../utils/supabaseClient";
import { useLocalizedMenuItem } from "../../../../../hooks/useLocalizedMenuItem";
import { useLocalizedCategory } from "../../../../../hooks/useLocalizedCategory";

interface ItemBreakdownData {
    itemId: string;
    menuItem?: MenuItem;
    categoryName?: string;
    orders: number;
    revenue: number;
    aiOrders: number;
    aiRevenue: number;
}

interface ItemBreakdownTableProps {
    data: ItemBreakdownData[];
    isLoading?: boolean;
    title?: string;
    formatCurrency?: (value: number) => string;
    branchId: string;
}

const TableSkeleton: React.FC = () => (
    <Card>
        <div className="flex items-center gap-2 mb-6">
            <Skeleton height="h-5" width="w-5" rounded="rounded" />
            <Skeleton height="h-6" width="w-40" />
        </div>
        <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50">
                    <Skeleton height="h-12" width="w-12" rounded="rounded-lg" />
                    <div className="flex-1">
                        <Skeleton height="h-4" width="w-32" className="mb-2" />
                        <Skeleton height="h-3" width="w-20" />
                    </div>
                    <Skeleton height="h-4" width="w-16" />
                    <Skeleton height="h-4" width="w-20" />
                    <Skeleton height="h-4" width="w-16" />
                    <Skeleton height="h-4" width="w-20" />
                </div>
            ))}
        </div>
    </Card>
);

const ItemBreakdownRow = memo(({
    item,
    idx,
    branchId,
    totalRevenue,
    formatValue
}: {
    item: ItemBreakdownData;
    idx: number;
    branchId: string;
    totalRevenue: number;
    formatValue: (val: number) => string
}) => {
    const { t } = useTranslation();
    const { localizedName, menuItem } = useLocalizedMenuItem(branchId, item.itemId);
    const { localizedName: localizedCategoryName } = useLocalizedCategory(branchId, menuItem?.category_id);

    const revenuePercent = totalRevenue > 0
        ? ((item.revenue / totalRevenue) * 100).toFixed(1)
        : "0";
    const aiContribution = item.revenue > 0
        ? ((item.aiRevenue / item.revenue) * 100).toFixed(1)
        : "0";

    // Use cached menuItem's image if available, falling back to passed menuItem
    const displayImage = menuItem?.image_url || item.menuItem?.image_url;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ delay: idx * 0.03 }}
            className="group grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl transition-all duration-200 border border-transparent hover:border-primary/20 dark:hover:border-purple-500/30"
        >
            {/* Item Info */}
            <div className="col-span-4 flex items-center gap-3">
                <div className="relative w-12 h-12 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 flex-shrink-0 group-hover:ring-2 group-hover:ring-primary/30 transition-all">
                    {displayImage ? (
                        <img
                            src={displayImage}
                            alt={localizedName || "Item"}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <FiPackage className="w-5 h-5 text-gray-400" />
                        </div>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-gray-900 dark:text-white truncate">
                        {localizedName || item.itemId.slice(0, 8)}
                    </h4>
                    <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {localizedCategoryName || item.categoryName || t("analytics.itemBreakdown.unknown")}
                        </span>
                        <span className="text-xs text-primary font-medium">
                            {revenuePercent}% {t("analytics.itemBreakdown.ofTotal")}
                        </span>
                    </div>
                </div>
            </div>

            {/* Orders */}
            <div className="col-span-2 flex items-center justify-center md:justify-center">
                <div className="text-center">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                        {item.orders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 md:hidden">{t("analytics.itemBreakdown.orders")}</p>
                </div>
            </div>

            {/* Revenue */}
            <div className="col-span-2 flex items-center justify-center md:justify-center">
                <div className="text-center">
                    <p className="text-lg font-bold text-emerald-500">
                        {formatValue(item.revenue)}
                    </p>
                    <p className="text-xs text-gray-400 md:hidden">{t("analytics.itemBreakdown.revenue")}</p>
                </div>
            </div>

            {/* AI Orders */}
            <div className="col-span-2 flex items-center justify-center md:justify-center">
                <div className="text-center">
                    <p className="text-lg font-semibold text-purple-500 dark:text-purple-400">
                        {item.aiOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 md:hidden">{t("analytics.itemBreakdown.aiOrders")}</p>
                </div>
            </div>

            {/* AI Revenue */}
            <div className="col-span-2 flex items-center justify-center md:justify-center">
                <div className="text-center">
                    <div className="flex items-center justify-center gap-1">
                        <p className="text-lg font-semibold text-purple-500 dark:text-purple-400">
                            {formatValue(item.aiRevenue)}
                        </p>
                    </div>
                    {parseFloat(aiContribution) > 0 && (
                        <p className="text-xs text-purple-400">
                            {aiContribution}% {t("analytics.itemBreakdown.ai")}
                        </p>
                    )}
                </div>
            </div>
        </motion.div>
    );
});
ItemBreakdownRow.displayName = "ItemBreakdownRow";

const ItemBreakdownTable: React.FC<ItemBreakdownTableProps> = memo(({
    data,
    isLoading = false,
    title,
    formatCurrency,
    branchId,
}) => {
    const { t } = useTranslation();
    const displayTitle = title || t("analytics.itemBreakdown.title");

    if (isLoading) {
        return <TableSkeleton />;
    }

    if (!data || data.length === 0) {
        return (
            <Card>
                <div className="flex items-center gap-2 mb-4">
                    <FiPackage className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {displayTitle}
                    </h3>
                </div>
                <div className="flex flex-col items-center justify-center py-12">
                    <FiPackage className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">
                        {t("analytics.itemBreakdown.noData")}
                    </p>
                </div>
            </Card>
        );
    }

    // Calculate totals for percentage
    const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
    const formatValue = (amount: number) => {
        if (typeof formatCurrency === "function") {
            return formatCurrency(amount);
        }
        return `$${amount.toFixed(2)}`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
        >
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                            <FiPackage className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {displayTitle}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {data.length} {t("analytics.itemBreakdown.items")}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Table Header */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 mb-2 bg-gray-50 dark:bg-gray-800/50 rounded-xl">
                    <div className="col-span-4">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("analytics.itemBreakdown.item")}
                        </span>
                    </div>
                    <div className="col-span-2 text-center">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("analytics.itemBreakdown.orders")}
                        </span>
                    </div>
                    <div className="col-span-2 text-center">
                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t("analytics.itemBreakdown.revenue")}
                        </span>
                    </div>
                    <div className="col-span-2 text-center">
                        <span className="text-xs font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wider">
                            {t("analytics.itemBreakdown.aiOrders")}
                        </span>
                    </div>
                    <div className="col-span-2 text-center">
                        <span className="text-xs font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wider">
                            {t("analytics.itemBreakdown.aiRevenue")}
                        </span>
                    </div>
                </div>

                {/* Table Rows */}
                <div className="space-y-2">
                    <AnimatePresence>
                        {data.map((item, idx) => (
                            <ItemBreakdownRow
                                key={item.itemId}
                                item={item}
                                idx={idx}
                                branchId={branchId}
                                totalRevenue={totalRevenue}
                                formatValue={formatValue}
                            />
                        ))}
                    </AnimatePresence>
                </div>
            </Card>
        </motion.div>
    );
});

ItemBreakdownTable.displayName = "ItemBreakdownTable";

export default ItemBreakdownTable;
